import { useCallback, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { format } from 'date-fns';
//
import {
  Datepicker,
  MbscDatepickerChangeEvent,
} from '@akoom-tech/mobiscroll-react';
//
import { Box, Button, Stack, Typography } from '@mui/material';
import { LoadingButton } from '@mui/lab';
import { VBMCardTitle, VBMCardWrapper, VBMEditButton } from '..';
// hooks + utils
import useResponsive from '@/hooks/useResponsive';
import usePostBookingSessionUpdate from '@/features/bookings-management/hooks/usePostBookingSessionUpdate';
import { timePickerSx } from '@/features/users/providers/utils';
import { fDate } from '@/utils/formatTime';
// types
import { BookingSession } from '@/features/bookings-management/types';

type VBMDateTimeProps = {
  date: string;
  time: string;
  editable?: boolean;
  bookingSession?: BookingSession;
};

export default function VBMDateTime({
  date,
  time,
  editable = false,
  bookingSession,
}: VBMDateTimeProps) {
  const [isEdit, setIsEdit] = useState(false);
  const [searchParams] = useSearchParams();
  const downMd = useResponsive('down', 'md');
  const sessionID = searchParams.get('sessionID');
  const [dateLocalValue, setDate] = useState<string>(date);
  const [timeLocalValue, setTime] = useState<string>(time);

  const handleDateChange = useCallback((ev: MbscDatepickerChangeEvent) => {
    setDate(format(new Date(String(ev.valueText)), 'yyyy-MM-dd'));
  }, []);

  const handleTimeChange = useCallback((ev: MbscDatepickerChangeEvent) => {
    const selectedTime = ev.value;
    if (selectedTime instanceof Date) {
      setTime(format(new Date(selectedTime), 'HH:mm'));
    } else {
      setTime('');
    }
  }, []);

  const handleToggleEdit = (value: boolean) => setIsEdit(value);

  const { sessionUpdateMutation } = usePostBookingSessionUpdate({
    onSuccessFn: () => {
      handleToggleEdit(false);
    },
    successText: 'Time/date updated',
  });

  const handleSaveChanges = () => {
    sessionUpdateMutation.mutate({
      bookingSessionId: Number(sessionID),
      bookingSessionDto: {
        time: timeLocalValue,
        date: dateLocalValue,
        booking_cleaners:
          bookingSession?.booking_session_cleaners?.map(
            (cleaner) => cleaner.id
          ) ?? [],
      },
    });
  };

  const handleCancel = () => {
    setDate(date);
    setTime(time);
    handleToggleEdit(false);
  };

  return (
    <VBMCardWrapper>
      {!isEdit && editable && (
        <VBMEditButton onClickButton={() => setIsEdit(true)} />
      )}

      <VBMCardTitle title="Date & time" sx={{ mb: 1 }} />

      {isEdit ? (
        <>
          <Stack spacing={2}>
            <Box sx={timePickerSx}>
              <Datepicker
                name="VBMDateTime-mbscDatePicker"
                label="Date"
                theme="ios"
                themeVariant="light"
                controls={['calendar']}
                touchUi={true}
                display="anchored"
                animation="pop"
                value={dateLocalValue}
                onChange={handleDateChange}
                dateFormat="D MMM YYYY"
                buttons={[]}
              />
            </Box>
            <Box sx={timePickerSx}>
              <Datepicker
                name="VBMDateTime-mbscTimePicker"
                value={timeLocalValue}
                onChange={handleTimeChange}
                label="Time"
                theme="ios"
                themeVariant="light"
                controls={['timegrid']}
                display="anchored"
                touchUi={true}
                minTime="06:00"
                maxTime="23:00"
                stepMinute={30}
                animation="pop"
                timeFormat="HH:mm"
              />
            </Box>
          </Stack>

          <Stack
            direction="row"
            justifyContent="flex-end"
            spacing={1}
            sx={{ mt: 2 }}
          >
            <Button
              variant="outlined"
              size="small"
              color="inherit"
              onClick={handleCancel}
            >
              Cancel
            </Button>
            <LoadingButton
              variant="contained"
              size="small"
              onClick={handleSaveChanges}
              loading={sessionUpdateMutation.isLoading}
            >
              {downMd ? 'Save' : 'Save changes'}
            </LoadingButton>
          </Stack>
        </>
      ) : (
        <Typography variant="body2">
          {fDate(date, 'EE, dd MMM yyyy')} - {time}
        </Typography>
      )}
    </VBMCardWrapper>
  );
}
